import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, Trash2, Edit3, X, AtSign } from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { Comment, BoardMember } from '../types';
import { getCommentsForProduct, addComment, updateComment, deleteComment, addActivity } from '../firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { getMentionedUserIds, highlightMentions, getMentionSuggestions } from '../utils/mentionUtils';

interface CommentsProps {
  productId: string;
  boardId: string;
  isOpen: boolean;
  onClose: () => void;
  boardMembers?: BoardMember[];
}

const Comments: React.FC<CommentsProps> = ({ productId, boardId, isOpen, onClose, boardMembers = [] }) => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState<BoardMember[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen && productId) {
      loadComments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadComments is stable
  }, [isOpen, productId]);

  // Add escape key functionality
  useEscapeKey(onClose, isOpen);

  const loadComments = async () => {
    setIsLoading(true);
    try {
      const commentsData = await getCommentsForProduct(productId);
      setComments(commentsData);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;

    setIsSubmitting(true);
    try {
      // Get mentioned user IDs
      const mentionedUserIds = getMentionedUserIds(newComment, boardMembers);

      const commentId = await addComment({
        productId,
        boardId,
        content: newComment.trim(),
        createdBy: user.id,
        createdByEmail: user.email,
        mentions: mentionedUserIds,
      });

      // Add activity for comment
      await addActivity({
        boardId,
        productId,
        type: 'comment_added',
        description: `added a comment`,
        createdBy: user.id,
        createdByEmail: user.email,
      });

      // Send notifications to mentioned users
      mentionedUserIds.forEach((mentionedUserId: string) => {
        if (mentionedUserId !== user.id) {
          boardMembers.find(m => m.userId === mentionedUserId);
          addNotification({
            userId: mentionedUserId,
            type: 'mention',
            title: 'You were mentioned in a comment',
            message: `${user.email} mentioned you in a comment`,
            boardId,
            productId,
            commentId,
            createdBy: user.id,
            createdByEmail: user.email,
            isRead: false,
          });
        }
      });

      const newCommentObj: Comment = {
        id: commentId,
        productId,
        boardId,
        content: newComment.trim(),
        createdBy: user.id,
        createdByEmail: user.email,
        createdAt: new Date(),
        updatedAt: new Date(),
        mentions: mentionedUserIds,
      };

      setComments([...comments, newCommentObj]);
      setNewComment('');
      setShowSuggestions(false);
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!editContent.trim()) return;

    try {
      await updateComment(commentId, { content: editContent.trim() });
      setComments(comments.map(comment => 
        comment.id === commentId 
          ? { ...comment, content: editContent.trim(), updatedAt: new Date() }
          : comment
      ));
      setEditingComment(null);
      setEditContent('');
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await deleteComment(commentId);
      setComments(comments.filter(comment => comment.id !== commentId));
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const startEditing = (comment: Comment) => {
    setEditingComment(comment.id);
    setEditContent(comment.content);
  };

  const cancelEditing = () => {
    setEditingComment(null);
    setEditContent('');
  };

  const handleCommentChange = (value: string) => {
    setNewComment(value);
    
    // Handle mention suggestions
    const suggestions = getMentionSuggestions(value, boardMembers);
    setMentionSuggestions(suggestions);
    setShowSuggestions(suggestions.length > 0);
    setSuggestionIndex(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSuggestionIndex(prev => (prev + 1) % mentionSuggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSuggestionIndex(prev => (prev - 1 + mentionSuggestions.length) % mentionSuggestions.length);
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (mentionSuggestions[suggestionIndex]) {
          insertMention(mentionSuggestions[suggestionIndex]);
        }
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    }
  };

  const insertMention = (member: BoardMember) => {
    const displayName = member.name || member.email.split('@')[0];
    const beforeMention = newComment.substring(0, newComment.lastIndexOf('@'));
    const afterMention = newComment.substring(newComment.lastIndexOf('@') + 1);
    const newValue = beforeMention + '@' + displayName + ' ' + afterMention;
    setNewComment(newValue);
    setShowSuggestions(false);
    
    // Focus back to textarea
    if (textareaRef.current) {
      textareaRef.current.focus();
      const cursorPos = beforeMention.length + displayName.length + 2; // +2 for @ and space
      textareaRef.current.setSelectionRange(cursorPos, cursorPos);
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-white/15 backdrop-blur-xl z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="glass-effect rounded-3xl p-6 w-full max-w-2xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Comments</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Comments List */}
            <div className="flex-1 overflow-y-auto space-y-4 mb-6">
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-satsuma-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-gray-500">Loading comments...</p>
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-8">
                  <MessageCircle size={48} className="text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No comments yet</p>
                  <p className="text-sm text-gray-400">Be the first to add a comment!</p>
                </div>
              ) : (
                comments.map((comment) => (
                  <motion.div
                    key={comment.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white/15 rounded-xl p-4"
                  >
                    {editingComment === comment.id ? (
                      <div className="space-y-3">
                        <textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="w-full p-3 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-satsuma-500 focus:border-transparent"
                          rows={3}
                          placeholder="Edit your comment..."
                        />
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={cancelEditing}
                            className="text-sm text-gray-500 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleEditComment(comment.id)}
                            className="btn-primary text-sm px-3 py-1"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-satsuma-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                              {comment.createdByEmail.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-800">
                                {comment.createdByEmail}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatTimeAgo(comment.createdAt)}
                                {comment.updatedAt > comment.createdAt && ' (edited)'}
                              </p>
                            </div>
                          </div>
                          {user && comment.createdBy === user.id && (
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => startEditing(comment)}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                              >
                                <Edit3 size={14} />
                              </button>
                              <button
                                onClick={() => handleDeleteComment(comment.id)}
                                className="text-gray-400 hover:text-red-600 transition-colors p-1"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">
                          {highlightMentions(comment.content, boardMembers)}
                        </p>
                      </div>
                    )}
                  </motion.div>
                ))
              )}
            </div>

            {/* Add Comment Form */}
            {user && (
              <form onSubmit={handleSubmitComment} className="border-t border-white/20 pt-4">
                <div className="flex items-end space-x-3">
                  <div className="flex-1 relative">
                    <textarea
                      ref={textareaRef}
                      value={newComment}
                      onChange={(e) => handleCommentChange(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Add a comment... Use @ to mention someone"
                      className="w-full p-3 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-satsuma-500 focus:border-transparent"
                      rows={2}
                      disabled={isSubmitting}
                    />
                    
                    {/* Mention Suggestions */}
                    {showSuggestions && mentionSuggestions.length > 0 && (
                      <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-10">
                        {mentionSuggestions.map((member, index) => (
                          <button
                            key={member.userId}
                            type="button"
                            onClick={() => insertMention(member)}
                            className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center space-x-2 ${
                              index === suggestionIndex ? 'bg-satsuma-50' : ''
                            }`}
                          >
                            <AtSign size={14} className="text-satsuma-500" />
                            <div>
                              <div className="text-sm font-medium text-gray-800">
                                {member.name || member.email.split('@')[0]}
                              </div>
                              {member.name && (
                                <div className="text-xs text-gray-500">{member.email}</div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={!newComment.trim() || isSubmitting}
                    className="btn-primary p-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Comments;
